import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";

import { RateRepository } from "../../../rate/rate.repository";
import { Subject, Subscription, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { HttpParams } from "@angular/common/http";
import { PaginationList } from "../../../shared/grid/pagination-list.model";
import { BaseRate } from "../../../rate/rate-base.model";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";
import { Bind } from "primeng/bind";
import { AutoComplete } from "primeng/autocomplete";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "app-rate-select",
  templateUrl: "./rate-select.component.html",
  styleUrls: ["./rate-select.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, AutoComplete, FormsModule, AutocompleteEmptyMessageComponent],
})
export class RateSelectComponent implements OnDestroy {
  private rateRepository = inject(RateRepository);

  public readonly rateSelected = output<BaseRate>();
  @Input() public rate: BaseRate;
  public readonly discriminator = input<string>(null);
  public readonly direction = input<string>(null);
  public rateList: BaseRate[] = [];
  public subscription = new Subscription();
  public searchString = "";
  public readonly loading = signal(false);

  private searchParams = new Subject<HttpParams>();

  public constructor() {
    this.subscription.add(
      this.searchParams
        .pipe(
          switchMap((params: HttpParams) =>
            this.rateRepository
              .getPaginatedRateList(params)
              .pipe(catchError(() => of(null))),
          ),
        )
        .subscribe((rates: PaginationList<BaseRate> | null) => {
          this.rateList = rates ? rates.items : [];
          this.loading.set(false);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onRateChange(event: BaseRate) {
    this.rate = event;
    this.rateSelected.emit(this.rate);
  }

  public search(searchString) {
    this.searchString = searchString;
    this.buildRateList();
  }

  public getRateUrl() {
    if (this.rate && this.rate instanceof BaseRate) {
      return `/rate/${this.rate.getDiscriminator()}/${this.rate.getType()}/${this.rate.getId()}`;
    } else {
      return "";
    }
  }

  private buildRateList(): void {
    let params = new HttpParams({
      fromObject: {
        limit: "25",
        "criteria[valid]": "true",
        "criteria[type]": "contract",
        "sorting[name]": "asc",
        search: this.searchString,
      },
    });
    const discriminator = this.discriminator();
    if (discriminator) {
      params = params.append("criteria[discriminator]", discriminator);
    }
    const direction = this.direction();
    if (direction) {
      params = params.append("criteria[direction]", direction);
    }

    // new array reference opens the overlay so the loading message is visible
    this.rateList = [];
    this.loading.set(true);
    this.searchParams.next(params);
  }
}
